import { ProjectService } from '@/api/project';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { DeleteTestIssue } from '@/components/testIssue/DeleteTestIssue';
import { ExportTestIssue } from '@/components/testIssue/ExportTestIssue';
import { ImportTestIssue } from '@/components/testIssue/ImportTestIssue';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import React, { useRef } from 'react';
import { FaArchive, FaShare } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import moment from 'moment';
import { useAccess } from '@/utils/hooks/useAccess';
import { useIsExternal } from '@/utils/hooks/useUserType';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import {
  ITestIssueOptions,
  ITestIssueSchema,
  TestIssueService,
} from '@/api/testIssue';
import { SelectFieldRow } from '@/components/shared/tableRow/SelectFieldRow';
import { CustomCellProperties } from 'alurkerja-ui';
import { toastAlert } from '@/utils/libs/toastAlert';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { BiSolidShow } from 'react-icons/bi';
import { FaPen } from 'react-icons/fa6';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';

interface CustomCellProps extends CustomCellProperties {
  optionsData?: ITestIssueOptions;
  isReadOnly?: boolean;
}

const CustomCell: React.FC<CustomCellProps> = ({
  name,
  rowValue,
  value,
  defaultCell,
  optionsData,
  isReadOnly,
}) => {
  const { mutate: update } = TestIssueService.useUpdate();

  if (name === 'test_run') {
    if (!rowValue.test_run) return <>-</>;

    return (
      <Link
        to={`/project/${rowValue.project_id}/test-run/${rowValue.test_run_id}`}
        className="text-blue-600 hover:underline font-medium"
      >
        {rowValue.test_run.title}
      </Link>
    );
  } else if (name === 'assignee_issue') {
    return <>{rowValue.assignee ? rowValue.assignee.full_name : '-'}</>;
  } else if (name === 'created_by') {
    return <>{value ? value.full_name : '-'}</>;
  } else if (name === 'created_at') {
    return <>{moment(value).format('DD MMMM YYYY')}</>;
  } else if (name === 'priority') {
    if (isReadOnly) {
      return <>{rowValue.priority || '-'}</>;
    }

    return (
      <SelectFieldRow
        key={rowValue.id}
        value={rowValue.priority}
        onChange={value => {
          const toast = toastAlert();
          toast.info({ title: 'Waiting to update Priority' });

          update(
            { ...(rowValue as ITestIssueSchema), priority: value },
            {
              onSuccess: () => {
                toast.close();
                toast.success({ title: 'Success update Priority' });
              },
            },
          );
        }}
        options={optionsData?.priority ?? []}
      />
    );
  } else if (name === 'status') {
    if (isReadOnly) {
      return <>{rowValue.status || '-'}</>;
    }

    return (
      <SelectFieldRow
        key={rowValue.id}
        value={rowValue.status}
        onChange={value => {
          const toast = toastAlert();
          toast.info({ title: 'Waiting to update Status' });

          update(
            { ...(rowValue as ITestIssueSchema), status: value },
            {
              onSuccess: () => {
                toast.close();
                toast.success({ title: 'Success update Status' });
              },
            },
          );
        }}
        options={optionsData?.status ?? []}
      />
    );
  }

  return defaultCell;
};

interface CustomActionCellProps {
  data: ITestIssueSchema;
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
  isViewer?: boolean;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  tableRef,
  isViewer,
}) => {
  return (
    <>
      {!isViewer ? (
        <>
          <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />

          <LinkButton path={`${data.id}/edit`} icon={<FaPen size={15} />} />

          <DeleteTestIssue
            id={data.id!}
            onSuccess={() => tableRef.current?.refetch()}
          />
        </>
      ) : (
        <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />
      )}
    </>
  );
};

export const ListTestIssue: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const { data: optionsData } = TestIssueService.useGetOptions(id!);
  const isViewer = useAccess(id!);
  const isExternal = useIsExternal();
  const isReadOnly = isViewer || isExternal;

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);

  const { data: optionData } = TestIssueService.useGetDropDown(id);

  const spec = useCustomSpec('/api/crud/test-issue', {
    columns: [
      { key: 'code_issue', label: 'Code' },
      { key: 'title', label: 'Title' },
      { key: 'test_run', label: 'Test Run' },
      { key: 'created_by', label: 'Created by' },
      { key: 'assignee_issue', label: 'Assigned to' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const optionUser = optionData?.assignee_issue.map(item => ({
    label: item.label,
    value: item.label,
  }));

  const filterSpec: IFilterSpec[] = [
    { name: 'code_issue', label: 'Issue Code', type: 'textarea' },
    { name: 'title', label: 'Title', type: 'text' },
    {
      name: 'created_by',
      label: 'Created by',
      type: 'option',
      options: optionUser,
    },
    {
      name: 'assignee_to',
      label: 'Assigned To',
      type: 'option',
      options: optionUser,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'multiOption',
      options: optionData?.status,
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'option',
      options: optionData?.priority,
    },
    { name: 'created_at', label: 'Created Date', type: 'date' },
  ];

  const toShare = () => navigate(`/public/shareable/test-issue/${id}`);

  const toArchive = () => navigate('archive');

  if (isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Section
      title={`Issue - ${projectData?.title}`}
      actions={
        !isReadOnly ? (
          <div className="flex flex-row gap-3">
            <OutlineButton icon={<FaShare />} onClick={toShare}>
              Share
            </OutlineButton>

            <ExportTestIssue
              id={id!}
              projectName={projectData?.title}
              tableRef={ref}
            />

            <ImportTestIssue project_id={id}></ImportTestIssue>

            <OutlineButton icon={<FaArchive />} onClick={toArchive}>
              Archive
            </OutlineButton>

            <CreateButton label="Add Issue" />
          </div>
        ) : (
          <></>
        )
      }
    >
      <ExtendedTableLowCode
        ref={ref}
        defaultFilter={{
          project_id: id,
          is_archive: 'false',
        }}
        defaultSort="code_issue"
        defaultSortOrder="desc"
        filterSpec={filterSpec}
        showFilter
        spec={spec}
        customButtonCreate={HideCustomCreateButton}
        customActionCell={data => (
          <CustomActionCell
            data={data as ITestIssueSchema}
            tableRef={ref}
            isViewer={isReadOnly}
          />
        )}
        customCell={props => (
          <CustomCell
            {...props}
            optionsData={optionsData}
            isReadOnly={isReadOnly}
          />
        )}
      />
    </Section>
  );
};
